import { callApi } from './api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { OrderNotificationChannels } from '@/models/order';
import { CourseCrmClientDetail, CourseCrmImportReport, CourseCrmListResult, CourseCrmShippingStatus, CrmEnrollmentType } from '@/models/course-crm';

// Admin-only. Every route under `/course-crm` is admin-role gated server-side.
const courseCrmApiUrl = import.meta.env.VITE_API_URL_V1 + '/course-crm';

export interface CourseCrmFilters {
    page: number;
    limit: number;
    search?: string;
    type?: CrmEnrollmentType;
    /** A cohort id, or the literal `unassigned`. */
    cohortId?: string;
    status?: CourseCrmShippingStatus;
}

export const getCourseCrmClients = async (filters: CourseCrmFilters) => {
    const params = new URLSearchParams({ page: String(filters.page), limit: String(filters.limit) });
    // An empty string must NOT be sent: the API reads a present-but-empty filter
    // as a real constraint and would match nobody.
    if (filters.search) params.set('search', filters.search);
    if (filters.type) params.set('type', filters.type);
    if (filters.cohortId) params.set('cohortId', filters.cohortId);
    if (filters.status) params.set('status', filters.status);
    return callApi<CourseCrmListResult>({ url: `${courseCrmApiUrl}/clients?${params.toString()}`, method: 'GET' });
};

export const useCourseCrmClients = (filters: CourseCrmFilters) =>
    useQuery({
        // Filters are part of the key, so changing one refetches instead of
        // showing the previous filter's rows under the new label.
        queryKey: ['course-crm-clients', filters],
        queryFn: () => getCourseCrmClients(filters),
    });

export const getCourseCrmClient = async (userId: string) =>
    callApi<CourseCrmClientDetail>({ url: `${courseCrmApiUrl}/clients/${userId}`, method: 'GET' });

export const useCourseCrmClient = (userId?: string, enabled = true) =>
    useQuery({
        queryKey: ['course-crm-client', userId],
        queryFn: () => getCourseCrmClient(userId as string),
        enabled: !!userId && enabled,
    });

export interface CourseCrmClientPayload {
    enrollmentType?: CrmEnrollmentType | '';
    cohort?: string;
    referral?: string;
    linkedinUrl?: string;
    telegramHandle?: string;
    telegramJoined?: boolean;
    testimonialVideo?: boolean;
    testimonialPost?: boolean;
    notes?: string;
}

export interface CourseCrmShipmentPayload {
    status?: CourseCrmShippingStatus;
    trackingCode?: string;
    carrier?: string;
    trackingUrl?: string;
    needsAttention?: boolean;
    note?: string;
}

/** Invalidate both the row and the list, so the table never shows a stale status. */
const invalidate = (queryClient: ReturnType<typeof useQueryClient>, userId: string) => {
    queryClient.invalidateQueries({ queryKey: ['course-crm-clients'] });
    queryClient.invalidateQueries({ queryKey: ['course-crm-client', userId] });
};

export const useUpdateCourseCrmClient = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ userId, ...payload }: CourseCrmClientPayload & { userId: string }) =>
            callApi<CourseCrmClientDetail>({ url: `${courseCrmApiUrl}/clients/${userId}`, method: 'PUT', data: payload }),
        onSuccess: (_d, v) => invalidate(queryClient, v.userId),
    });
};

export const useUpdateCourseCrmShipment = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ userId, ...payload }: CourseCrmShipmentPayload & { userId: string }) =>
            callApi<CourseCrmClientDetail>({ url: `${courseCrmApiUrl}/clients/${userId}/shipment`, method: 'PUT', data: payload }),
        onSuccess: (_d, v) => invalidate(queryClient, v.userId),
    });
};

export const useUpdateCourseCrmNotifications = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ userId, ...payload }: OrderNotificationChannels & { userId: string }) =>
            callApi<OrderNotificationChannels>({ url: `${courseCrmApiUrl}/clients/${userId}/notifications`, method: 'PUT', data: payload }),
        onSuccess: (_d, v) => invalidate(queryClient, v.userId),
    });
};

export const useImportCourseCrm = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (payload: { csv: string; dryRun: boolean; createMissingUsers: boolean }) =>
            callApi<CourseCrmImportReport>({ url: `${courseCrmApiUrl}/import`, method: 'POST', data: payload }),
        onSuccess: (_d, v) => {
            // A dry run changed nothing — refetching would only cost a request.
            if (!v.dryRun) queryClient.invalidateQueries({ queryKey: ['course-crm-clients'] });
        },
    });
};
